import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import ConsentimientoFirmaPad from './ConsentimientoFirmaPad';
import {
  TEXTO_CONSENTIMIENTO_DEFAULT,
  CONSENTIMIENTO_PLACEHOLDERS,
  getPlantillaConsentimientoConsultorio,
  savePlantillaConsentimientoConsultorio,
  renderDocumentoConsentimiento,
  aplicarPlaceholdersPlantilla,
  buildDatosPacienteConsent,
  firmaConsentimientoValida,
  labelModoFirmaConsentimiento,
  etiquetaIdentificacionPacienteConsent,
  pacienteUsaTextoPersonalizadoConsent,
} from '../../data/consentimientoInformado';
import {
  prepareAttachmentForSave,
  resolveClinicalAttachment,
  isPdfDataUrl,
  isImageDataUrl,
} from '../../utils/clinicalFileStorage';
import { readFileAsDataUrl } from '../../utils/readClinicalFile';

function DocumentoLegal({ texto, identificacion, fecha, nombrePaciente, consultorioNombre, esPreview }) {
  return (
    <article
      className={`consent-doc-legal${esPreview ? ' consent-doc-legal--preview' : ''}`}
      id="consentimiento-documento-impresion"
    >
      <header className="consent-doc-legal__hdr">
        <div className="consent-doc-legal__seal" aria-hidden>
          <i className="bi bi-file-earmark-text-fill" />
        </div>
        <div>
          <h2 className="consent-doc-legal__title">Consentimiento informado</h2>
          <p className="consent-doc-legal__meta">Actuaciones odontológicas — documento legal</p>
          {consultorioNombre ? (
            <p className="consent-doc-legal__consultorio">
              <i className="bi bi-building" /> {consultorioNombre}
            </p>
          ) : null}
        </div>
      </header>
      <div className="consent-doc-legal__datos">
        {nombrePaciente ? (
          <div className="consent-doc-legal__dato">
            <span className="consent-doc-legal__dato-lbl">Paciente</span>
            <span className="consent-doc-legal__dato-val">{nombrePaciente}</span>
          </div>
        ) : null}
        {identificacion ? (
          <div className="consent-doc-legal__dato">
            <span className="consent-doc-legal__dato-lbl">Identificación</span>
            <span className="consent-doc-legal__dato-val">{identificacion}</span>
          </div>
        ) : null}
        {fecha ? (
          <div className="consent-doc-legal__dato">
            <span className="consent-doc-legal__dato-lbl">Fecha del documento</span>
            <span className="consent-doc-legal__dato-val">{fecha}</span>
          </div>
        ) : null}
      </div>
      <div className="consent-doc-legal__body">
        {texto.split('\n').map((line, i) => (
          <p key={i} className={line.trim() === '' ? 'consent-doc-legal__spacer' : ''}>
            {line.trim() === '' ? '\u00A0' : line}
          </p>
        ))}
      </div>
    </article>
  );
}

function PlaceholderChips({ onInsert, disabled }) {
  return (
    <div className="consent-placeholders">
      <p className="consent-placeholders__hint">
        Inserte variables que se completan automáticamente con los datos del paciente al generar el documento:
      </p>
      <div className="consent-placeholders__chips">
        {CONSENTIMIENTO_PLACEHOLDERS.map((p) => (
          <button
            key={p.key}
            type="button"
            className="consent-placeholder-chip"
            disabled={disabled}
            title={`${p.label} — ej. ${p.ejemplo}`}
            onClick={() => onInsert(p.key)}
          >
            {p.key}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ConsentimientoInformadoSection({
  form,
  setForm,
  readOnly = false,
  tenantId = null,
  idDoctor = null,
}) {
  const update = useCallback(
    (key, value) => {
      if (setForm) setForm((f) => ({ ...f, [key]: value }));
    },
    [setForm]
  );

  const [vista, setVista] = useState('documento');
  const [plantillaTexto, setPlantillaTexto] = useState(TEXTO_CONSENTIMIENTO_DEFAULT);
  const [nombreConsultorio, setNombreConsultorio] = useState('');
  const [personalizarPaciente, setPersonalizarPaciente] = useState(() => pacienteUsaTextoPersonalizadoConsent(form));
  const [mensaje, setMensaje] = useState('');

  const firmaPadRef = useRef(null);
  const plantillaTextareaRef = useRef(null);
  const pacienteTextareaRef = useRef(null);
  const [firmaPreviewUrl, setFirmaPreviewUrl] = useState('');
  const [guardandoFirma, setGuardandoFirma] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPlantillaConsentimientoConsultorio(tenantId, idDoctor).then((p) => {
      if (!cancelled) {
        setPlantillaTexto(p.texto);
        setNombreConsultorio(p.nombreConsultorio);
      }
    });
    return () => { cancelled = true; };
  }, [tenantId, idDoctor]);

  const idConsentDoc = useMemo(
    () => etiquetaIdentificacionPacienteConsent(form),
    [form.tipoIdentificacion, form.cedula]
  );

  const datosPaciente = useMemo(
    () => buildDatosPacienteConsent(form, nombreConsultorio),
    [form, nombreConsultorio]
  );

  const [textoDocumentoRenderizado, setTextoDocumentoRenderizado] = useState('');

  useEffect(() => {
    let cancelled = false;
    renderDocumentoConsentimiento(form, tenantId, idDoctor).then((t) => {
      if (!cancelled) setTextoDocumentoRenderizado(t);
    });
    return () => { cancelled = true; };
  }, [form, tenantId, idDoctor, nombreConsultorio]);

  const previewPlantilla = useMemo(
    () => aplicarPlaceholdersPlantilla(plantillaTexto, datosPaciente),
    [plantillaTexto, datosPaciente]
  );

  const fechaDisplay = form.consentimientoInformadoFecha
    ? new Date(form.consentimientoInformadoFecha).toLocaleDateString('es-EC', { dateStyle: 'long' })
    : new Date().toLocaleDateString('es-EC', { dateStyle: 'long' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const resolved = await resolveClinicalAttachment({
        dataUrl: form.consentimientoInformadoFirmaDataUrl,
        storageId: form.consentimientoInformadoFirmaStorageId,
      });
      if (!cancelled) setFirmaPreviewUrl(resolved.dataUrl || '');
    })();
    return () => {
      cancelled = true;
    };
  }, [form.consentimientoInformadoFirmaDataUrl, form.consentimientoInformadoFirmaStorageId]);

  const insertarEnTexto = (textoActual, setTexto, ref, token) => {
    const el = ref?.current;
    const cur = String(textoActual ?? '');
    const start = el ? el.selectionStart ?? cur.length : cur.length;
    const end = el ? el.selectionEnd ?? start : start;
    const next = `${cur.slice(0, start)}${token}${cur.slice(end)}`;
    setTexto(next);
    if (el) {
      const pos = start + token.length;
      requestAnimationFrame(() => {
        el.setSelectionRange(pos, pos);
        el.focus();
      });
    }
  };

  const onPlantillaChange = (e) => setPlantillaTexto(e.target.value);

  const guardarPlantillaConsultorio = async () => {
    if (!tenantId && !idDoctor) {
      window.alert('No se identificó el consultorio.');
      return;
    }
    const ok = await savePlantillaConsentimientoConsultorio(tenantId, plantillaTexto, nombreConsultorio, idDoctor);
    if (ok) {
      setMensaje('Plantilla guardada para este consultorio. Todos los pacientes usarán este criterio por defecto.');
      if (!personalizarPaciente) {
        update('consentimientoInformadoTexto', '');
      }
    }
  };

  const restaurarPlantillaDefault = () => {
    if (window.confirm('¿Restaurar el texto legal predeterminado del sistema?')) {
      setPlantillaTexto(TEXTO_CONSENTIMIENTO_DEFAULT);
    }
  };

  const aplicarPlantillaAlPaciente = () => {
    if (window.confirm('¿Usar solo la plantilla del consultorio para este paciente? Se borrará la personalización.')) {
      setPersonalizarPaciente(false);
      update('consentimientoInformadoTexto', '');
      setMensaje('Este paciente usará la plantilla del consultorio con sus datos.');
    }
  };

  const onPersonalizarToggle = (checked) => {
    setPersonalizarPaciente(checked);
    if (checked && !pacienteUsaTextoPersonalizadoConsent(form)) {
      update('consentimientoInformadoTexto', plantillaTexto);
    }
    if (!checked) {
      update('consentimientoInformadoTexto', '');
    }
  };

  const aplicarFirma = async (dataUrl, modo, meta = {}) => {
    setGuardandoFirma(true);
    setMensaje('');
    try {
      const bytes = meta.byteSize || meta.sizeBytes || 0;
      const prep = await prepareAttachmentForSave(dataUrl, {
        ...meta,
        byteSize: bytes,
        category: 'firma_consentimiento',
      });
      setForm((f) => ({
        ...f,
        consentimientoInformadoFirmaDataUrl: prep.dataUrl,
        consentimientoInformadoFirmaStorageId: prep.storageId,
        consentimientoInformadoFirmaModo: modo,
        consentimientoInformadoFirmaBytes: prep.byteSize || bytes,
      }));
      setFirmaPreviewUrl(dataUrl);
      setMensaje('Firma guardada correctamente.');
    } catch (err) {
      window.alert(err?.message || 'No se pudo guardar la firma.');
    } finally {
      setGuardandoFirma(false);
    }
  };

  const registrarFirmaDibujo = () => {
    const pad = firmaPadRef.current;
    if (!pad?.hasInk?.()) {
      window.alert('Dibuje la firma en el recuadro antes de guardar.');
      return;
    }
    aplicarFirma(pad.toDataURL(), 'dibujo', { mime: 'image/png', nombre: 'firma-digital.png' });
  };

  const onFirmaArchivoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const { dataUrl, mime, nombre, esPdf, sizeBytes } = await readFileAsDataUrl(file, {
        maxImageBytes: 3 * 1024 * 1024,
        maxPdfBytes: 12 * 1024 * 1024,
      });
      await aplicarFirma(dataUrl, esPdf ? 'archivo_pdf' : 'archivo', { mime, nombre, sizeBytes });
    } catch (err) {
      window.alert(err.message || 'No se pudo cargar el archivo.');
    }
  };

  const quitarFirma = async () => {
    if (form.consentimientoInformadoFirmaStorageId) {
      const { deleteClinicalFile } = await import('../../utils/clinicalFileStorage');
      await deleteClinicalFile(form.consentimientoInformadoFirmaStorageId);
    }
    setForm((f) => ({
      ...f,
      consentimientoInformadoFirmaDataUrl: '',
      consentimientoInformadoFirmaStorageId: '',
      consentimientoInformadoFirmaModo: '',
      consentimientoInformadoFirmaBytes: 0,
      consentimientoInformado: false,
    }));
    setFirmaPreviewUrl('');
    firmaPadRef.current?.reset?.();
  };

  const onAceptacionChange = async (checked) => {
    if (!checked) {
      update('consentimientoInformado', false);
      return;
    }
    if (!idConsentDoc.ok) {
      window.alert('Complete tipo y número de identificación en «Información personal».');
      return;
    }
    const firmaOk = firmaConsentimientoValida(firmaPreviewUrl || form.consentimientoInformadoFirmaDataUrl);
    if (!firmaOk) {
      Swal.fire({
        icon: 'warning',
        title: 'Firma requerida',
        text: 'Registre la firma antes de declarar conformidad.',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#002A54',
      });
      return;
    }
    const textoFirmado = await renderDocumentoConsentimiento(form, tenantId, idDoctor);
    update('consentimientoInformadoTexto', textoFirmado);
    update('consentimientoInformado', true);
    const hoy = new Date().toISOString().slice(0, 10);
    if (!form.consentimientoInformadoFecha) update('consentimientoInformadoFecha', hoy);
    setMensaje('Consentimiento registrado con el texto y datos del paciente al momento de la firma.');
  };

  const imprimirDocumento = () => {
    const el = document.getElementById('consentimiento-documento-impresion');
    if (!el) return;
    const w = window.open('', '_blank');
    if (!w) {
      window.alert('Permita ventanas emergentes para imprimir.');
      return;
    }
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Consentimiento informado</title>
      <style>
        body{font-family:Georgia,'Times New Roman',serif;max-width:720px;margin:2rem auto;line-height:1.55;font-size:12pt;color:#1e293b}
        h2{text-align:center;font-size:14pt;margin:0 0 .5rem}
        .consent-doc-legal__datos{display:grid;grid-template-columns:1fr 1fr;gap:.5rem;margin:1rem 0;padding:1rem;border:1px solid #ddd;background:#fafafa}
        .consent-doc-legal__dato-lbl{display:block;font-size:9pt;text-transform:uppercase;color:#64748b}
        .consent-doc-legal__dato-val{font-weight:600}
        p{margin:0 0 .65rem}
      </style></head><body>${el.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const firmaUrl = firmaPreviewUrl || form.consentimientoInformadoFirmaDataUrl || '';
  const tieneFirma = firmaConsentimientoValida(firmaUrl);
  const nombreConsultorioDisplay = nombreConsultorio || 'Consultorio';

  if (readOnly) {
    return (
      <div className="consentimiento-section consentimiento-section--readonly">
        <DocumentoLegal
          texto={textoDocumentoRenderizado}
          identificacion={idConsentDoc.ok ? idConsentDoc.texto : ''}
          fecha={fechaDisplay}
          nombrePaciente={form.nombre}
          consultorioNombre={nombreConsultorioDisplay}
        />
        <div className="fo-consent-firma-block">
          <h6 className="fo-consent-firma-title">
            <i className="bi bi-pen" />
            Firma del paciente
          </h6>
          {tieneFirma ? (
            <>
              <span className="fo-consent-firma-modo">
                {labelModoFirmaConsentimiento(form.consentimientoInformadoFirmaModo)}
              </span>
              {isImageDataUrl(firmaUrl) ? (
                <img src={firmaUrl} alt="Firma del paciente" className="fo-consent-firma-img" />
              ) : isPdfDataUrl(firmaUrl) ? (
                <embed src={firmaUrl} type="application/pdf" className="fo-consent-firma-pdf" title="PDF firmado" />
              ) : null}
            </>
          ) : (
            <p className="fo-consent-firma-empty">Sin firma adjunta.</p>
          )}
        </div>
        <p className="consent-readonly-estado mb-0">
          <strong>Estado:</strong>{' '}
          {form.consentimientoInformado ? (
            <span className="text-success">Consentimiento aceptado</span>
          ) : (
            <span className="text-muted">Pendiente</span>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="consentimiento-section consentimiento-section--editor">
      {!idConsentDoc.ok ? (
        <Alert variant="warning" className="small py-2 mb-3">
          Complete <strong>tipo y número de identificación</strong> en Información personal. Las variables del
          documento se rellenarán con esos datos.
        </Alert>
      ) : null}

      {mensaje ? (
        <Alert variant="success" className="small py-2 mb-3" onClose={() => setMensaje('')} dismissible>
          {mensaje}
        </Alert>
      ) : null}

      <div className="consent-editor-nav" role="tablist">
        {[
          { id: 'documento', label: 'Documento del paciente', icon: 'bi-file-person' },
          { id: 'plantilla', label: 'Plantilla del consultorio', icon: 'bi-building' },
          { id: 'firma', label: 'Firma', icon: 'bi-pen' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={vista === t.id}
            className={`consent-editor-nav__btn${vista === t.id ? ' consent-editor-nav__btn--active' : ''}`}
            onClick={() => setVista(t.id)}
          >
            <i className={`bi ${t.icon}`} />
            {t.label}
          </button>
        ))}
      </div>

      {vista === 'plantilla' && (
        <div className="consent-editor-panel">
          <div className="consent-editor-panel__intro">
            <i className="bi bi-building" />
            <div>
              <strong>Plantilla única de su consultorio</strong>
              <p className="mb-0 small text-muted">
                Redacte el criterio legal que aplica a todos los pacientes. Use variables como{' '}
                <code>{'{{nombre}}'}</code> para insertar datos automáticamente al abrir cada ficha.
              </p>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Nombre del consultorio (aparece en el documento)</Form.Label>
            <Form.Control
              size="sm"
              value={nombreConsultorio}
              onChange={(e) => setNombreConsultorio(e.target.value)}
              placeholder="Ej. Clínica Dental Sonrisa"
            />
          </Form.Group>

          <div className="consent-editor-split">
            <div className="consent-editor-split__edit">
              <Form.Label className="small fw-semibold">Texto de la plantilla</Form.Label>
              <Form.Control
                ref={plantillaTextareaRef}
                as="textarea"
                rows={16}
                className="consent-editor-textarea"
                value={plantillaTexto}
                onChange={onPlantillaChange}
              />
              <PlaceholderChips
                onInsert={(token) => insertarEnTexto(plantillaTexto, setPlantillaTexto, plantillaTextareaRef, token)}
              />
              <div className="d-flex flex-wrap gap-2 mt-3">
                <Button variant="primary" size="sm" type="button" onClick={guardarPlantillaConsultorio}>
                  <i className="bi bi-cloud-check me-1" />
                  Guardar plantilla del consultorio
                </Button>
                <Button variant="outline-secondary" size="sm" type="button" onClick={restaurarPlantillaDefault}>
                  Restaurar texto predeterminado
                </Button>
              </div>
            </div>
            <div className="consent-editor-split__preview">
              <p className="consent-editor-split__preview-lbl">Vista previa con datos de este paciente</p>
              <DocumentoLegal
                texto={previewPlantilla}
                identificacion={idConsentDoc.ok ? idConsentDoc.texto : ''}
                fecha={fechaDisplay}
                nombrePaciente={form.nombre}
                consultorioNombre={nombreConsultorio || 'Su consultorio'}
                esPreview
              />
            </div>
          </div>
        </div>
      )}

      {vista === 'documento' && (
        <div className="consent-editor-panel">
          <div className="consent-editor-panel__intro consent-editor-panel__intro--doc">
            <i className="bi bi-file-earmark-person" />
            <div>
              <strong>Documento con datos del paciente</strong>
              <p className="mb-0 small text-muted">
                Se genera desde la plantilla del consultorio. Puede personalizar el texto solo para este paciente si
                lo requiere un caso especial.
              </p>
            </div>
          </div>

          <label
            className={`consent-personalizar-toggle${personalizarPaciente ? ' consent-personalizar-toggle--on' : ''}`}
            htmlFor="consent-personalizar-paciente"
          >
            <input
              type="checkbox"
              id="consent-personalizar-paciente"
              className="consent-personalizar-toggle__input"
              checked={personalizarPaciente}
              onChange={(e) => onPersonalizarToggle(e.target.checked)}
            />
            <span className="consent-personalizar-toggle__check" aria-hidden>
              {personalizarPaciente ? <i className="bi bi-check-lg" /> : null}
            </span>
            <span className="consent-personalizar-toggle__text">
              <strong>Personalizar documento solo para este paciente</strong>
              <small>Activa un texto legal distinto al de la plantilla del consultorio.</small>
            </span>
          </label>

          {personalizarPaciente ? (
            <div className="consent-editor-split consent-editor-split--single mb-3">
              <div className="consent-editor-split__edit">
                <Form.Label className="small fw-semibold">Texto personalizado (admite variables)</Form.Label>
                <Form.Control
                  ref={pacienteTextareaRef}
                  as="textarea"
                  rows={12}
                  className="consent-editor-textarea"
                  value={form.consentimientoInformadoTexto ?? ''}
                  onChange={(e) => update('consentimientoInformadoTexto', e.target.value)}
                />
                <PlaceholderChips
                  onInsert={(token) =>
                    insertarEnTexto(
                      form.consentimientoInformadoTexto,
                      (v) => update('consentimientoInformadoTexto', v),
                      pacienteTextareaRef,
                      token
                    )
                  }
                />
                <Button variant="link" size="sm" className="p-0 mt-1" type="button" onClick={aplicarPlantillaAlPaciente}>
                  Volver a usar solo la plantilla del consultorio
                </Button>
              </div>
            </div>
          ) : null}

          <DocumentoLegal
            texto={textoDocumentoRenderizado}
            identificacion={idConsentDoc.ok ? idConsentDoc.texto : ''}
            fecha={fechaDisplay}
            nombrePaciente={form.nombre}
            consultorioNombre={nombreConsultorioDisplay}
            esPreview
          />

          <div className="d-flex flex-wrap gap-2 mt-3">
            <Button variant="outline-dark" size="sm" type="button" onClick={imprimirDocumento}>
              <i className="bi bi-printer me-1" />
              Imprimir / PDF
            </Button>
            <Button variant="outline-primary" size="sm" type="button" onClick={() => setVista('plantilla')}>
              <i className="bi bi-building me-1" />
              Editar plantilla del consultorio
            </Button>
          </div>
        </div>
      )}

      {vista === 'firma' && (
        <div className="consent-editor-panel">
          <p className="small text-muted mb-3">
            Firma del paciente: dibujo en pantalla, imagen escaneada o PDF del documento firmado en papel. No consume
            cuota de radiografías.
          </p>

          {tieneFirma ? (
            <div className="consent-firma-preview-box mb-3">
              <div className="small text-muted mb-1">{labelModoFirmaConsentimiento(form.consentimientoInformadoFirmaModo)}</div>
              {isImageDataUrl(firmaUrl) ? (
                <img src={firmaUrl} alt="Firma" className="fo-consent-firma-img" />
              ) : (
                <embed src={firmaUrl} type="application/pdf" className="fo-consent-firma-pdf" title="PDF" />
              )}
              <Button variant="link" size="sm" className="text-danger p-0 mt-2" type="button" onClick={quitarFirma}>
                Borrar firma
              </Button>
            </div>
          ) : null}

          <div className="consent-firma-modes">
            <div className="consent-firma-mode">
              <h6 className="consent-firma-mode__title">
                <i className="bi bi-pencil" /> Firmar en pantalla
              </h6>
              <ConsentimientoFirmaPad ref={firmaPadRef} disabled={guardandoFirma} />
              <div className="d-flex flex-wrap gap-2 mt-2">
                <Button variant="outline-secondary" size="sm" type="button" onClick={() => firmaPadRef.current?.reset?.()}>
                  Limpiar
                </Button>
                <Button variant="primary" size="sm" type="button" disabled={guardandoFirma} onClick={registrarFirmaDibujo}>
                  {guardandoFirma ? 'Guardando…' : 'Guardar firma'}
                </Button>
              </div>
            </div>
            <div className="consent-firma-mode">
              <h6 className="consent-firma-mode__title">
                <i className="bi bi-upload" /> Subir imagen o PDF
              </h6>
              <Form.Control
                type="file"
                accept="image/*,.pdf,application/pdf"
                size="sm"
                disabled={guardandoFirma}
                onChange={onFirmaArchivoChange}
              />
              <Form.Text muted className="small d-block mt-1">
                JPG, PNG o PDF (hasta 12 MB).
              </Form.Text>
            </div>
          </div>
        </div>
      )}

      <div className="consent-aceptacion-footer">
        <label
          className={`consent-aceptacion-toggle${form.consentimientoInformado ? ' consent-aceptacion-toggle--on' : ''}`}
          htmlFor="chk-consentimiento-acepto"
        >
          <input
            type="checkbox"
            id="chk-consentimiento-acepto"
            className="consent-aceptacion-toggle__input"
            checked={!!form.consentimientoInformado}
            onChange={(e) => onAceptacionChange(e.target.checked)}
          />
          <span className="consent-aceptacion-toggle__check" aria-hidden>
            {form.consentimientoInformado ? <i className="bi bi-check-lg" /> : null}
          </span>
          <span className="consent-aceptacion-toggle__text">
            <strong>Declaración del paciente</strong>
            <span>
              El paciente declara haber leído el documento con sus datos, comprenderlo y prestar su
              consentimiento informado con la firma registrada.
            </span>
          </span>
        </label>

        <div className="consent-aceptacion-fecha">
          <label className="consent-aceptacion-fecha__label" htmlFor="consentimiento-fecha">
            <i className="bi bi-calendar3" aria-hidden />
            Fecha del consentimiento
          </label>
          <Form.Control
            type="date"
            id="consentimiento-fecha"
            className="consent-aceptacion-fecha__input"
            value={form.consentimientoInformadoFecha ? String(form.consentimientoInformadoFecha).slice(0, 10) : ''}
            onChange={(e) => update('consentimientoInformadoFecha', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}
